import { Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MarcaDTO, MarcaResponseDTO } from '../../../models/marca.model';
import { MarcaService } from '../../../services/marca/marca.service';
import { SafeStorageService } from '../../safe-storage.service';

@Component({
  selector: 'app-select-marca',
  templateUrl: './select-marca.component.html',
  styleUrls: ['./select-marca.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectMarcaComponent),
      multi: true
    }
  ]
})
export class SelectMarcaComponent
  implements ControlValueAccessor, OnInit, OnDestroy {

  /* ======================= *
   * Inputs
   * ======================= */
  @Input() placeholder: string = '';
  @Input() returnType: 'object' | 'id' = 'object';
  @Input() showAddButton: boolean = true;

  /* ======================= *
   * Estado interno
   * ======================= */
  env = environment;
  idEmpresa: string = '';
  marcas: MarcaDTO[] = [];
  loading = false;
  search = '';
  page = 0;
  size = 20;
  totalRecords = 0;
  hasMore = true; // Flag para indicar se há mais registros
  isLoadingMore = false; // Flag para carregamento incremental

  /** valor que o p-dropdown usa */
  value: any = null;

  /** Visibilidade do dialog de nova marca */
  dialogVisible: boolean = false;

  /** Texto do botao "+ Agregar Marca" (carregado via i18n) */
  textoAgregarMarca: string = '';

  private langSub?: Subscription;

  /* ======================= *
   * ControlValueAccessor
   * ======================= */
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  constructor(
    private safe: SafeStorageService,
    private marcaService: MarcaService,
    private translate: TranslateService
  ) { }

  /* ======================= *
   * Lifecycle
   * ======================= */
  ngOnInit(): void {
    this.idEmpresa = this.safe.getItem('id') || '';
    this.loadInitial(); // Carrega dados iniciais
    this.loadTextoAgregar();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTextoAgregar();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /* ======================= *
   * ControlValueAccessor impl
   * ======================= */
  writeValue(value: any): void {
    this.value = value;

    // Se já recebeu valor (edição) e a lista já existe, sincroniza
    if (value && this.marcas.length) {
      this.syncSelectedValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /* ======================= *
   * Seleção
   * ======================= */
  onSelect(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  /* ======================= *
   * Carga inicial
   * ======================= */
  loadInitial(): void {
    this.loading = true;
    this.page = 0;
    this.hasMore = true;
    this.marcas = []; // Limpa lista existente

    this.marcaService.get('/marca/filtrarMarca', {
      page: this.page,
      size: this.size,
      sort: 'id,asc',
      filter: `&nome=${this.search}`
    }).subscribe({
      next: (res: MarcaResponseDTO) => {
        this.marcas = [...res.content];
        this.totalRecords = res.totalElements || 0;
        this.hasMore = this.marcas.length < this.totalRecords;
        this.syncSelectedValue();
      },
      error: () => {
        // Marcas são opcionais — falha silenciosa
        this.marcas = [];
        this.hasMore = false;
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }


  /* ======================= *
   * Filtro remoto
   * ======================= */
  onFilter(event: any): void {
    this.search = event.filter?.trim() || '';
    this.loadInitial(); // Reinicia busca com novo filtro
  }

  /* ======================= *
   * Sincronização edição
   * ======================= */
  private syncSelectedValue(): void {
    if (!this.value) return;

    // quando retorna id, garantir que o marca exista na lista
    if (this.returnType === 'id') {
      const exists = this.marcas.some(g => g.id === this.value);
      if (!exists) {
        this.loadMarcaById(this.value);
      }
    }
  }

  private loadMarcaById(id: number): void {
    this.marcaService.get(`/marca/${id}`).subscribe({
      next: (marca: any) => {
        this.marcas = [marca, ...this.marcas];
      },
      error: (err) => {
        if (err?.status === 404) {
          console.warn(`Marca com ID ${id} não encontrado.`);
          return;
        }
        console.error('Erro ao carregar marca por ID:', err);
      }
    });
  }

  /* ======================= *
   * Dialog de nova marca
   * ======================= */

  /**
   * Handler do click no footer "+ Agregar Marca".
   * Usa (mousedown) com stopPropagation para impedir
   * que o dropdown feche antes de processar o clique.
   */
  onAddNew(event: MouseEvent): void {
    event.stopPropagation();
    this.dialogVisible = true;
  }

  /**
   * Handler chamado quando o dialog emite uma marca criada.
   * Insere a marca no inicio da lista e a seleciona automaticamente.
   */
  onMarcaCriada(marca: MarcaDTO): void {
    // Inserir a marca no inicio da lista
    this.marcas = [marca, ...this.marcas];
    this.totalRecords += 1;

    // Selecionar a marca recem-criada
    if (this.returnType === 'id') {
      this.value = marca.id;
    } else {
      this.value = marca;
    }

    // Propagar a mudanca para o formulario pai
    this.onChange(this.value);
  }

  /* ======================= *
   * Util
   * ======================= */
  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src =
      'assets/images/profile_placeholder.png';
  }

  private loadTextoAgregar(): void {
    this.textoAgregarMarca = this.translate.instant('select-marca.botoes.agregar_marca');
  }
}